/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Button, TextField, Box, FormHelperText, FormControl, Alert } from '@mui/material';

import { useDispatch } from 'react-redux';
import { LuiSelect } from '@jda/lui-common-component-library-mui5';

import useStyle from './styles';
import Loader from '../../shared/Loader';
import { getEntitlementServiceIdps } from '../../services/user';
import { LABELS } from '../UserManagement/constant';
import FormDialog from '../../shared/Dialog/FormDialog';
import { compareByProperty } from '../../utility/commonUtility';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
import { KEY_NAMES, USER_FORM_FIELDS } from '../UserManagement/constant';
import { getAllRoles } from '../../services/roles';
import { IAddRoleMappingDialogProps, IRole } from '../../models/Roles/RolesContracts';
import { getRoleListAction, setHasNoRolesAction } from '../../store/role/roleActionCreator';
import {
  TITLE,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  ADD_ROLE_MAPPING,
  EDIT_ROLE_MAPPING,
  ROLE_MAPPING_CREATION_FAILED,
  ROLE_MAPPING_UPDATION_FAILED,
  ROLE_MAPPING_MESSAGE,
  LOCAL_ROLE_NAME_REQUIRED,
  IDENTITY_PROVIDER_REQUIRED,
  LOCAL_ROLE_DROP_DOWN_HELPER_TEXT,
  ROLE_MAPPING_CREATED_SUCCESSFULLY,
  ROLE_NAME_IDENTITY_PROVIDER_REQUIRED,
  ROLE_MAPPING_UPDATED_SUCCESSFULLY
} from './constant';
import { SearchableList } from '../../shared/CustomList';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { AutocompleteChangeDetails, AutocompleteChangeReason } from '../../shared/CustomList/types';
import { createNewRoleMapping, updateRoleMapping } from '../../services/roleMapping';
import { DROPDOWN_MAX_HEIGHT } from '../../shared/PolicyDialog/constants';
import { IIDP } from '../../models/Users/UserContracts';
const formDefaultValues = {
  idpId: '',
  localRoleName: '',
  roleNameFromIDP: '',
  byRoleId: 0
};

const formErrorDefaultValues = {
  error: false,
  errorHelperText: ''
};
const AddRoleMappingDialog = ({
  edit,
  editData,
  openModal,
  openCloseModal,
  onSave,
  displaySnackbar
}: IAddRoleMappingDialogProps) => {

  const formEditValues = {
    roleNameFromIDP: editData?.name,
    localRoleName: editData?.realmName,
    idpId: editData?.idpId,
    byRoleId: editData?.byRoleId
  };

  const { classes } = useStyle();
  const dispatch = useDispatch();
  const [idpData, setidpData] = useState<IIDP[]>([]);
  const [showLoader, showHideLoader] = useState(false);
  const [roleList, setRoleList] = useState<IRole[]>([]);
  const [formValues, updateFormValues] = useState<any>(edit ? formEditValues : formDefaultValues);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [formLocalRoleNameError, updateFormLocalRoleNameError] = useState(formErrorDefaultValues);
  const [formRoleNameFromIDPError, updateFormRoleNameFromIDPError] = useState(formErrorDefaultValues);
  const [formIdentityProviderError, updateFormIdentityProviderError] = useState(formErrorDefaultValues);
  const [isDirty, setIsDirty] = useState(!edit);

  const idpList = useMemo(() => {
    const sortedIdpData = [...idpData].sort(compareByProperty());

    return sortedIdpData.map(idp => ({ ...idp, key: idp.id, displayName: idp.name }));
  }, [idpData]);

  useEffect(() => {
    getRoles();
    getEntitlementServiceIdps()
      .then(response => {
        if (response) {
          let idpDataNew = getEntitlementServiceIdpsDataSorted(response);
          if (edit) {
            idpDataNew.forEach((item: any) => {
              if (item.id === editData.idpId) {
                updateFormValues({ ...formValues, idpId: item.id });
              }
            })
          }
          setidpData(idpDataNew);
          if (idpDataNew.length === 1) {
            idpTypeChangeHandler(idpDataNew[0].id);
          }
        }
      })
      .catch((_error: any) => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEntitlementServiceIdpsDataSorted = (res: any) => {
    return res.idps;
  };

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const idpTypeChangeHandler = (idpId: string) => {
    updateFormValues({ ...formValues, idpId });
    setIsDirty(true);
    updateFormIdentityProviderError(formErrorDefaultValues);
  };

  const updateFormFieldsRoleNameFromIdp = (e: { target: { value: string } }) => {
    updateFormValues({ ...formValues, roleNameFromIDP: e.target.value });
    updateFormRoleNameFromIDPError(formErrorDefaultValues);
  };

  const getRoles = () => {
    showHideLoader(true);
    getAllRoles()
      .then((response: IRole[]) => {
        response
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item: IRole) => (item.priority = Math.floor(Math.random() * 5)));
        dispatch(getRoleListAction(response));
        showHideLoader(false);
        if (response.length === 0) {
          dispatch(setHasNoRolesAction(true));
        } else {
          setRoleList(response);
          if (edit) {
            response?.forEach(data => {
              if (data?.id === editData.byRoleId) {
                updateFormValues({ ...formValues, localRoleName: data?.name, byRoleId: data?.id });
              }
            })

          }
        }
      })
      .catch(_error => {
        const errorMessage = {
          message: interpolateString([TITLE])
        };
        setErrors(errorMessage);
      });
  };

  const handleRoleItemClick = (
    _event: React.ChangeEvent<{}>,
    _value: any,
    _reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined
  ) => {
    updateFormValues({ ...formValues, localRoleName: details?.option?.name || '', byRoleId: details?.option?.id });
    updateFormLocalRoleNameError(formErrorDefaultValues);
  };

  const getSelectedItem = (formFieldKey: string, key: string, itemList: any) => {
    const value = getFormFieldValue(formFieldKey);
    const selItem = itemList.filter((item: any) => {
      return (edit && !isDirty) ? item[key] === editData[formFieldKey] : item[key] === value;
    });
    return Array.isArray(selItem) && selItem.length > 0 ? selItem[0] : '';
  };

  const getFormFieldValue = (fieldName: string) => {
    return formValues[fieldName];
  };

  const validateRequired = (values: any) => {
    if (!values.idpId || !values.roleNameFromIDP || !values.byRoleId) return false;
    return true;
  };

  const handleSubmit = () => {
    if (validateRequired(formValues)) {
      showHideLoader(true);
      const data = {
        name: formValues.roleNameFromIDP,
        idpId: formValues.idpId,
        byRoleId: formValues.byRoleId,
        id: edit ? editData.id : 0
      };
      const onAdd = edit ? updateRoleMapping : createNewRoleMapping
      onAdd(data).then(response => {
        if (response) {
          const msg = edit ? ROLE_MAPPING_UPDATED_SUCCESSFULLY : ROLE_MAPPING_CREATED_SUCCESSFULLY;
          showHideLoader(false);
          displaySnackbar(interpolateString([response.name], msg), 'success');
          openCloseModal(false);
        }
        onSave();
      }).catch((error: any) => {
        const msg = edit ? ROLE_MAPPING_UPDATION_FAILED : ROLE_MAPPING_CREATION_FAILED;
        showHideLoader(false);
        displaySnackbar(interpolateString([error.message], msg), 'error');
      });

    } else {
      if (formValues.idpId === '' || !formValues.idpId) {
        updateFormIdentityProviderError({
          ...formIdentityProviderError,
          error: true,
          errorHelperText: IDENTITY_PROVIDER_REQUIRED
        });
      }
      if (formValues.roleNameFromIDP === '' || !formValues.roleNameFromIDP) {
        updateFormRoleNameFromIDPError({
          ...formRoleNameFromIDPError,
          error: true,
          errorHelperText: ROLE_NAME_IDENTITY_PROVIDER_REQUIRED
        });
      }
      if (formValues.localRoleName === '' || !formValues.localRoleName) {
        updateFormLocalRoleNameError({
          ...formLocalRoleNameError,
          error: true,
          errorHelperText: LOCAL_ROLE_NAME_REQUIRED
        });
      }
    }
  };

  return (
    <FormDialog
      maxWidth={'sm'}
      openModal={openModal}
      dialogTitle={edit ? EDIT_ROLE_MAPPING : ADD_ROLE_MAPPING}
      handleClose={handleClose}
      dialogAction1={
        <Button variant="outlined" name="cancelBtn" color="primary" disableElevation onClick={handleClose}>
          {CANCEL_BTN_TEXT}
        </Button>
      }
      dialogAction2={
        <Button
          color="primary"
          name={'saveBtn'}
          disableElevation
          variant="contained"
          disabled={showLoader}
          onClick={handleSubmit}
        >
          {SAVE_BTN_TEXT}
        </Button>
      }
    >
      <Alert
        elevation={0}
        severity={'info'}
        variant={'outlined'}
        className={classes.roleMappingAlert}
      >
        {ROLE_MAPPING_MESSAGE}
      </Alert>
      <FormControl fullWidth error={formIdentityProviderError.error}>
        <Box className={classes.dropDownText}>
          <LuiSelect
            id={USER_FORM_FIELDS.IDP_ID}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            data-testid={USER_FORM_FIELDS.IDP_ID}
            inputLabel={LABELS.IDENTITY_PROVIDER}
            onSelectionChange={event => {
              idpTypeChangeHandler(event.target.value.key);
            }}
            itemData={idpList}
            disabled={idpList.length === 1 ? true : false}
            selectedItem={getSelectedItem(USER_FORM_FIELDS.IDP_ID, KEY_NAMES.ID, idpList)}
          />
          {formIdentityProviderError.error ? (
            <FormHelperText error id={'form-identity-provider-error'}>
              {formIdentityProviderError.errorHelperText}
            </FormHelperText>
          ) : null}
        </Box>
      </FormControl>
      <Box className={classes.dropDownText}>
        <TextField
          fullWidth
          disabled={showLoader}
          id="role-name-from-idp"
          name={'roleNameFromIdp'}
          className={classes.modalTextBox}
          variant="standard"
          label={LABELS.ROLE_NAME_FROM_IDP}
          data-testid={'role-name-from-idp'}
          error={formRoleNameFromIDPError.error}
          defaultValue={formValues.roleNameFromIDP}
          onChange={updateFormFieldsRoleNameFromIdp}
        />
        {formRoleNameFromIDPError.error ? (
          <FormHelperText error id={'form-role-name-from-idp-error'}>
            {formRoleNameFromIDPError.errorHelperText}
          </FormHelperText>
        ) : null}
      </Box>
      <Box className={classes.dropDownText}>
        {roleList.length ? (
          <FormControl fullWidth error={formIdentityProviderError.error}>
            <SearchableList
              id={'autocomplete-search-for-local-role-name'}
              classes={classes}
              loading={showLoader}
              value={getSelectedItem(USER_FORM_FIELDS.ROLE_ID, KEY_NAMES.ID, roleList)}
              title={LABELS.LOCAL_ROLE_NAME}
              handleChange={handleRoleItemClick}
              error={formLocalRoleNameError.error}
              options={roleList?.sort(compareByProperty())}
            />
          </FormControl>
        ) : null}
        {formLocalRoleNameError.error ? (
          <FormHelperText error id={'form-local-role-name-error'}>
            {formLocalRoleNameError.errorHelperText}
          </FormHelperText>
        ) : (
          <FormHelperText>{LOCAL_ROLE_DROP_DOWN_HELPER_TEXT}</FormHelperText>
        )}
      </Box>
      {errors && <ErrorMessage {...errors} />}
      <Loader showLoader={showLoader} />
    </FormDialog>
  );
};

export default AddRoleMappingDialog;
